import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Briefcase, Mail, Power } from 'lucide-react';
import { api } from '../../api/client';
import { loadSession } from '../../auth/auth';
import { ConfirmDialog } from '../../components/ConfirmDialog';

/**
 * Knjigovođe (ADR-052) - spisak cele platforme.
 *
 * Knjigovođa nije klijent nijednog prodavca, pa prodavac ovu stranu ne vidi
 * (server odbija, `sysadmin-rbac.ts`). Gašenje naloga i ponovno slanje
 * potvrde sme samo admin platforme; gašenje važi odmah, i za firme koje su
 * trenutno otvorene.
 */
type Accountant = {
  id: string;
  email: string;
  fullName: string;
  agencyName: string;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  tenantCount: number;
};

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('sr-RS', { dateStyle: 'medium', timeStyle: 'short' }) : '-');

export function AccountantsPage() {
  const [rows, setRows] = useState<Accountant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [toggleTarget, setToggleTarget] = useState<Accountant | null>(null);
  const isAdmin = loadSession()?.admin.role === 'admin';

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<{ accountants: Accountant[] }>('/admin/accountants');
      setRows(r.accountants);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const resend = async (a: Accountant) => {
    setError(null);
    try {
      await api.post(`/admin/accountants/${a.id}/resend-verification`, {});
      setInfo(`Link za potvrdu je ponovo poslat na ${a.email}.`);
    } catch (e: any) {
      setError(e?.data?.message || 'Slanje nije uspelo.');
    }
  };

  const toggle = async () => {
    if (!toggleTarget) return;
    await api.post(`/admin/accountants/${toggleTarget.id}/active`, { isActive: !toggleTarget.isActive });
    refresh();
  };

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Briefcase size={22} /> Knjigovođe
        </h1>
        <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
          Nalozi knjigovođa. Firme ih dodaju po emailu; knjigovođa vidi firmu tek kad potvrdi email, i samo čita.
        </div>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {info && <div style={infoBox}>{info}</div>}

      <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg)' }}>
                <th style={th}>Knjigovođa</th>
                <th style={th}>Email</th>
                <th style={{ ...th, textAlign: 'right' }}>Firmi</th>
                <th style={th}>Registrovana</th>
                <th style={th}>Poslednja prijava</th>
                <th style={th}>Status</th>
                {isAdmin && <th style={{ ...th, textAlign: 'right' }}>Akcije</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: 'var(--color-text-strong)' }}>{a.fullName}</div>
                    {a.agencyName && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{a.agencyName}</div>}
                  </td>
                  <td style={td}>{a.email}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{a.tenantCount}</td>
                  <td style={td}>{fmt(a.createdAt)}</td>
                  <td style={td}>{fmt(a.lastLoginAt)}</td>
                  <td style={td}>
                    {!a.isActive
                      ? <span style={{ ...pill, background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>Ugašen</span>
                      : a.emailVerified
                        ? <span style={{ ...pill, background: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>Aktivan</span>
                        : <span style={{ ...pill, background: 'rgba(234,179,8,0.15)', color: '#eab308' }}>Email nije potvrđen</span>}
                  </td>
                  {isAdmin && (
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {a.isActive && !a.emailVerified && (
                        <button onClick={() => resend(a)} style={iconBtn} title="Ponovo pošalji potvrdu"><Mail size={15} /></button>
                      )}
                      <button onClick={() => setToggleTarget(a)} style={iconBtn} title={a.isActive ? 'Ugasi nalog' : 'Uključi nalog'}><Power size={15} /></button>
                    </td>
                  )}
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td style={{ ...td, textAlign: 'center', color: 'var(--color-text-muted)' }} colSpan={isAdmin ? 7 : 6}>Nema registrovanih knjigovođa.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {toggleTarget && (
        <ConfirmDialog
          title={toggleTarget.isActive ? 'Ugasiti nalog knjigovođe?' : 'Uključiti nalog knjigovođe?'}
          message={toggleTarget.isActive
            ? `${toggleTarget.fullName} (${toggleTarget.email}) odmah gubi pristup svim firmama, i onima koje su trenutno otvorene. Veze sa firmama ostaju.`
            : `${toggleTarget.fullName} ponovo vidi firme koje su je dodale.`}
          confirmLabel={toggleTarget.isActive ? 'Ugasi' : 'Uključi'}
          variant={toggleTarget.isActive ? 'danger' : 'default'}
          onConfirm={toggle}
          onClose={() => setToggleTarget(null)}
        />
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const pill: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const infoBox: React.CSSProperties = { padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.12)', color: '#86efac', fontSize: 13, marginBottom: 16 };
